import type {
  AdminGetRoutesLocationsResDto,
  AdminGetRoutesResDto,
} from "service/api/estabrookSchemas";
import { isSkippedStop, stopServiceMinutes } from "./stop-service-minutes";

type StopTiming = {
  arrivalMinutes: number;
  completionMinutes: number;
};

/** Rebuild stop timings in the same order as the route's travel and service. */
export const getRouteStopTimings = (
  locations: AdminGetRoutesLocationsResDto[],
): Record<string, StopTiming> => {
  const serviceMinutes = stopServiceMinutes(locations);
  const timings: Record<string, StopTiming> = {};
  let elapsedMinutes = 0;

  locations.forEach((location) => {
    if (isSkippedStop(location)) return;

    elapsedMinutes += location.travelDetails?.travelTimeBetweenLocations ?? 0;
    const arrivalMinutes = elapsedMinutes;
    elapsedMinutes +=
      serviceMinutes[location.locationId] ?? location.serviceTime ?? 0;

    timings[location.locationId] = {
      arrivalMinutes,
      completionMinutes: elapsedMinutes,
    };
  });

  return timings;
};

/** Ended routes retain their stop timings after the route-level ETA resets. */
export const getRouteEstimatedTimeMinutes = (
  route: Pick<AdminGetRoutesResDto, "state" | "locations" | "travelDetails">,
  adjustments?: {
    serviceMinutes: Record<string, number>;
    arrivalMinutes: Record<string, number>;
  },
): number | undefined => {
  if (route.state !== "ended") {
    const estimate = route.travelDetails?.estimatedTimeMinutes;
    if (estimate === undefined || !adjustments || route.state === "planning") {
      return estimate;
    }

    const locations = (route.locations ?? []).filter(
      (location) => !isSkippedStop(location),
    );
    const lastStop = locations[locations.length - 1];
    if (!lastStop) return estimate;

    // The last stop's adjusted arrival already includes all preceding travel
    // and service delays. Add its own service overrun once to get completion.
    const arrival = lastStop.travelDetails?.estimatedTimeMinutes;
    const adjustedArrival = adjustments.arrivalMinutes[lastStop.locationId];
    const arrivalDelay =
      arrival !== undefined && adjustedArrival !== undefined
        ? Math.max(0, adjustedArrival - arrival)
        : 0;
    const service = stopServiceMinutes(locations)[lastStop.locationId] ?? 0;
    const adjustedService = adjustments.serviceMinutes[lastStop.locationId];
    const serviceDelay =
      adjustedService !== undefined
        ? Math.max(0, adjustedService - service)
        : 0;

    return estimate + arrivalDelay + serviceDelay;
  }

  const locations = route.locations ?? [];
  const timings = getRouteStopTimings(locations);
  let lastFinishedMinutes: number | undefined;

  locations.forEach((location) => {
    if (isSkippedStop(location)) return;

    if (
      location.driverStatus === "completed" ||
      location.driverStatus === "partiallyCompleted" ||
      location.driverStatus === "cantPlow"
    ) {
      lastFinishedMinutes = Math.max(
        lastFinishedMinutes ?? 0,
        timings[location.locationId].completionMinutes,
      );
    }
  });

  return lastFinishedMinutes;
};
